import type { PriceListUsage } from "../../api/user";
import {
  formatTariffPeriodRange,
  formatTariffPrice,
  formatTariffSiteAmount,
  getTariffUsageRemainderRows,
} from "../../utils/userTariffFormat";
import {
  formatCheckPacksSummary,
  formatRequestedPurchaseTitle,
  getRequestedTariffBadge,
} from "../../utils/userLimitsFormat";
import {
  getProfileTariffBadge,
  getProfileTariffTitle,
  getTariffTimelineStatus,
  getVisibleTariffs,
} from "../../utils/tariffTimeline";
import "./AdminUserTariffCell.scss";

interface AdminUserTariffBlockProps {
  tariff: PriceListUsage;
  badge?: string;
  showUsageRemainders?: boolean;
  showPeriod?: boolean;
  mode: "active" | "requested" | "archived";
}

const AdminUserTariffBlock = ({
  tariff,
  badge,
  showUsageRemainders,
  showPeriod = true,
  mode,
}: AdminUserTariffBlockProps) => {
  const usageRows = showUsageRemainders ? getTariffUsageRemainderRows(tariff) : [];
  const isChecks = tariff.tariffType === "checks";
  const title =
    mode === "requested" || isChecks
      ? formatRequestedPurchaseTitle(tariff)
      : tariff.tariffName?.trim() || "—";

  return (
    <div className="admin-user-tariff-block">
      <div className="admin-user-tariff-block-head">
        <span className="admin-user-tariff-block-title">{title}</span>
        {badge ? (
          <span
            className={`admin-user-tariff-badge${
              badge === "Активен"
                ? " admin-user-tariff-badge--active"
                : badge === "Ожидает оплаты"
                  ? " admin-user-tariff-badge--pending"
                  : ""
            }`}
          >
            {badge}
          </span>
        ) : null}
      </div>
      <div className="admin-user-tariff-details">
        <span>{isChecks ? "Докупка" : "Тариф"}</span>
        <span>{formatTariffPrice(tariff)}</span>
        {isChecks ? (
          <span>{formatCheckPacksSummary(tariff.checkPacks)}</span>
        ) : (
          <span>Сайтов: {formatTariffSiteAmount(tariff)}</span>
        )}
        {showPeriod && !isChecks ? <span>{formatTariffPeriodRange(tariff)}</span> : null}
        {usageRows.map((row) => (
          <span key={row.label}>
            {row.label}: {row.value}
          </span>
        ))}
      </div>
    </div>
  );
};

interface AdminUserTariffCellProps {
  tariffs?: PriceListUsage[];
  mode: "active" | "requested" | "archived";
}

export const AdminUserTariffCell = ({ tariffs = [], mode }: AdminUserTariffCellProps) => {
  const visibleTariffs =
    mode === "active" ? getVisibleTariffs(tariffs) : mode === "archived" ? tariffs : tariffs;

  if (visibleTariffs.length === 0) {
    return <span className="admin-user-tariff-empty">—</span>;
  }

  return (
    <div className="admin-user-tariff-cell">
      {visibleTariffs.map((tariff, index) => {
        const status = getTariffTimelineStatus(tariff);
        const badge =
          mode === "requested"
            ? getRequestedTariffBadge(tariff)
            : mode === "archived"
              ? "Архив"
              : getProfileTariffBadge(tariff, status);

        return (
          <AdminUserTariffBlock
            key={`${tariff.payId ?? tariff.pricelistId ?? "tariff"}-${tariff.startAt ?? index}-${mode}`}
            tariff={tariff}
            badge={badge ?? getProfileTariffTitle(tariff, status)}
            showUsageRemainders={mode === "active" && status === "active"}
            showPeriod={mode !== "requested"}
            mode={mode}
          />
        );
      })}
    </div>
  );
};
